import React from 'react';
import { Box, Typography, Button, Grid, Paper, Stack, Divider } from '@mui/material';
import { Description, PictureAsPdf } from '@mui/icons-material';
import api from '../../services/api';
import { useEstimateData } from '../../contexts/EstimateDataContext';

interface SummaryValues {
  labor: number;       // robocizna
  materials: number;   // materiały
  equipment: number;   // sprzęt
  simplified: number;  // pozycje uproszczone
  indirect: number;    // koszty pośrednie
  purchase: number;    // koszty zakupu
  profit: number;      // zysk
  netto: number;       // wartość kosztorysowa netto
  vatRate: number;     // stawka VAT (np. 0.23)
}

const format = (val: number) => val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SummaryTab: React.FC<{ summary?: Partial<SummaryValues> }> = ({
  summary = {},
}) => {
  // Placeholder values jeżeli nie przekazano
  const {
    labor = 0,
    materials = 0,
    equipment = 0,
    simplified = 0,
    indirect = 0,
    purchase = 0,
    profit = 0,
    netto = 0,
    vatRate = 0.23,
  } = summary;

  const vat = netto * vatRate;
  const brutto = netto + vat;

  const { data: contextData, setSectionData } = useEstimateData();
  // na bieżąco aktualizuj summary w contextcie
  React.useEffect(()=>{ setSectionData('summary', { labor, materials, equipment, simplified, indirect, purchase, profit, netto, vatRate }); }, [labor,materials,equipment,simplified,indirect,purchase,profit,netto,vatRate,setSectionData]);

  const handleGenerateSheet = async () => {
    try {
      const { data } = await api.post('/sheets/generate', contextData);
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        alert('Wygenerowano arkusz, ale nie otrzymano linku.');
      }
    } catch (err:any) {
      console.error(err);
      alert('Błąd podczas generowania arkusza.');
    }
  };

  const handleGeneratePdf = () => {
    // TODO: wywołanie backendu z generowaniem PDF
    alert('Generowanie PDF w przygotowaniu...');
  };

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Podsumowanie po lewej */}
        <Grid item xs={12} sm={9} md={10}>
          <Paper elevation={3} sx={{ p: 0, maxWidth: 480, mr: 'auto', overflow: 'hidden', borderRadius: 2 }}>
            {/* kolorowy header */}
            <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', py: 1.5, px: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Podsumowanie kosztorysu</Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Stack spacing={1.2} divider={<Divider flexItem />}>
                <SummaryRow label="Wartość robocizny" value={labor} />
                <SummaryRow label="Wartość materiałów" value={materials} />
                <SummaryRow label="Wartość sprzętu" value={equipment} />
                <SummaryRow label="Pozycje uproszczone" value={simplified} />
                <SummaryRow label="Koszty pośrednie" value={indirect} />
                <SummaryRow label="Koszty zakupu" value={purchase} />
                <SummaryRow label="Zysk" value={profit} />
                <Divider />
                <Box sx={{ 
                  color: '#020242',
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px'
                }}>
                  <Typography sx={{ 
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}>
                    Wartość kosztorysowa: {format(netto)} zł netto
                  </Typography>
                </Box>
                <SummaryRow label={`Podatek VAT ${(vatRate*100).toFixed(0)}%`} value={vat} />
                <SummaryRow label="Wartość kosztorysowa brutto" value={brutto} bold big />
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* Przyciski po prawej */}
        <Grid item xs={12} sm={3} md={2}>
          <Stack spacing={2} alignItems="flex-end">
            <Button
              variant="contained"
              startIcon={<Description />}
              color="primary"
              onClick={handleGenerateSheet}
              fullWidth
            >
              Generuj Google Sheet
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdf />}
              color="secondary"
              onClick={handleGeneratePdf}
              fullWidth
            >
              Generuj PDF
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

interface RowProps { label: string; value: number; bold?: boolean; big?: boolean; }
const SummaryRow: React.FC<RowProps> = ({ label, value, bold, big }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: bold ? 600 : 400 }}>
    <Typography sx={{ whiteSpace:'nowrap' }}>{label}</Typography>
    <Typography sx={{ fontSize: big ? '1.1rem' : '0.9rem', fontWeight: bold ? 600 : 400 }}>{format(value)} zł</Typography>
  </Box>
);

export default SummaryTab; 